import uuidv4 from "uuid/v4";
import {
  RECENT_NOTIFICATION_UPDATE,
  RECENT_CHAT_DATA,
  RECENT_RECALL_UPDATE,
  ACTIVE_CHAT_DATA,
  ACTIVE_CHAT_UPDATE,
  PROFILE_IMAGE_CHAT_DATA,
  RECENT_REMOVE_MESSAGE_UPDATE,
  RECENT_ROSTER_UPDATE,
  DELETE_SINGLE_CHAT,
  RESET_TO_DEFAULT,
  UPDATE_ROSTER_LAST_MESSAGE,
  UPDATE_GROUP_NAME,
  RESET_RECENT_CHAT,
  UPDATE_MSG_BY_LAST_MSGID,
  MODAL_ACTIVE_CLASS,
  RECONNECT_RECENT_CHAT_UPDATE,
  UPDATE_MUTE_RECENT_CHAT,
  UPDATE_ARCHIVE_RECENT_CHAT,
  UNREAD_MESSAGE_DELETE,
  UPDATE_MESSAGE_STATUS,
  MARK_CHAT_MESSAGES_SEEN,
  CLEAR_ALL_CHAT,
  UPDATE_ACTIVE_CHAT_DATA,
  UPDATE_MESSAGE_REACTION,
  CLEAR_CHAT_HISTORY
} from "../Actions/Constants";
import { updateRecall, emptyMessage } from "../Helpers/Chat/Recall";
import { MEDIA_MESSAGE_TYPES } from "../Helpers/Constants";
import { getLoggedInUserIdSafe, getMsgStatusInOrder, transformReactionsGrouped, updateReactionList } from "../Helpers/Chat/ChatHelper";
import { getUserIdFromJid } from "../Helpers/Utility";
import { MSG_SEEN_STATUS_ID } from "../Helpers/Chat/Constant";


/**
 * Deduplicates an array of recent chat entries by their conversation identifier (fromUserId).
 * For group chats the SDK maps the group UUID to fromUserId, so two API entries that belong
 * to the same group will share the same (normalized) fromUserId.  When duplicates are found
 * we keep the entry whose timestamp is the highest (i.e. the most recent message).
 *
 * This guard is applied unconditionally to the incoming payload so that the initial load,
 * server-reconnect, and pagination paths are all protected.
 */
const dedupeRecentChatsByConversation = data => {
    if (!Array.isArray(data)) return data;
    const seen = {};
    for (const obj of data) {
        if (!obj.fromUserId) continue;
        const normalizedId =
            typeof obj.fromUserId === 'string' && obj.fromUserId.includes('@')
                ? obj.fromUserId.split('@')[0]
                : obj.fromUserId;
        if (!seen[normalizedId] || (seen[normalizedId].timestamp || 0) < (obj.timestamp || 0)) {
            seen[normalizedId] = { ...obj, fromUserId: normalizedId };
        }
    }
    return Object.values(seen);
};

const hasBodyContent = body => {
    if (!body || typeof body !== 'object') return false;
    // Check for text content
    if (typeof body.message === 'string' && body.message.trim() !== '') return true;
    // Check for media or other rich types
    if (body.media || body.contact || body.location || body.meet || body.sticker || body.thumbnail) return true;
    return false;
};

const updateRecentChat = (filterBy, newMessage, recentChatArray = []) => {
    const normalizedFilterBy = filterBy.includes('@') ? filterBy.split('@')[0] : filterBy;
    const checkalreadyExist = recentChatArray.find(message => {
        const messageId = message.fromUserId.includes('@') ? message.fromUserId.split('@')[0] : message.fromUserId;
        return messageId === normalizedFilterBy;
    });
    if (!checkalreadyExist) {
        newMessage.archiveStatus = 0;
        newMessage.fromUserId = normalizedFilterBy;
        return [...recentChatArray, newMessage];
    }
    return recentChatArray.map(recentItem => {
        const itemId = recentItem.fromUserId.includes('@')
            ? recentItem.fromUserId.split('@')[0]
            : recentItem.fromUserId;
        if (itemId === normalizedFilterBy) {
            const isSameMsg =
                newMessage.msgId && recentItem.msgId && String(newMessage.msgId) === String(recentItem.msgId);
            const statusTypes = [
                'Processing',
                'delivered',
                'carbonDelivered',
                'seen',
                'carbonSeen',
                'acknowledge',
                'carbonSentSeen',
            ];
            const isStatusType = statusTypes.includes(newMessage.msgType);
            const hasNewContent = hasBodyContent(newMessage.msgBody);

            return {
                ...recentItem,
                ...newMessage,
                fromUserId: normalizedFilterBy,
                // CRITICAL: Robust body/type persistence.
                // Prevent receipts (empty bodies) from overwriting real message content.
                msgBody: hasNewContent ? newMessage.msgBody : recentItem.msgBody || {},
                msgType:
                    newMessage.msgType && !isStatusType ? newMessage.msgType : recentItem.msgType || newMessage.msgType,
                // CRITICAL: Only preserve reaction metadata if it's the SAME message.
                // Reset metadata for new message IDs to fix the "Sticky Reaction" bug.
                reactions:
                    newMessage.reactions !== undefined
                        ? newMessage.reactions
                        : isSameMsg
                          ? recentItem.reactions
                          : undefined,
                reactedUserName:
                    newMessage.reactedUserName !== undefined
                        ? newMessage.reactedUserName
                        : isSameMsg
                          ? recentItem.reactedUserName
                          : undefined,
                emojiId:
                    newMessage.emojiId !== undefined ? newMessage.emojiId : isSameMsg ? recentItem.emojiId : undefined,
                messageSenderId:
                    newMessage.messageSenderId !== undefined
                        ? newMessage.messageSenderId
                        : isSameMsg
                          ? recentItem.messageSenderId
                          : undefined,
                deleteStatus:
                    newMessage.deleteStatus !== undefined
                        ? newMessage.deleteStatus
                        : isSameMsg
                          ? recentItem.deleteStatus
                          : 0,
                deletedBy:
                    newMessage.deletedBy !== undefined ? newMessage.deletedBy : isSameMsg ? recentItem.deletedBy : '',
                deleted_by:
                    newMessage.deleted_by !== undefined
                        ? newMessage.deleted_by
                        : isSameMsg
                          ? recentItem.deleted_by
                          : '',
                msgStatus: isSameMsg
                    ? newMessage.msgStatus !== undefined
                        ? newMessage.msgStatus
                        : recentItem.msgStatus
                    : newMessage.msgStatus !== undefined
                      ? newMessage.msgStatus
                      : 0,
            };
        }
        return recentItem;
    });
};

const updateMuteRecentChat = (data, stateData) => {
    return stateData.map(element => {
        if (element.fromUserId === data.fromUserId) {
            element.muteStatus = data.isMuted ? 1 : 0;
        }
        return element;
    });
};

const updateRecentUnreadCount = (data, stateData) => {
    const targetId = (data?.fromUserId || '').includes('@')
        ? data.fromUserId.split('@')[0]
        : data?.fromUserId || '';
    if (!targetId || !Array.isArray(stateData)) return stateData;
    return stateData.map(element => {
        const elementId = (element?.fromUserId || '').includes('@')
            ? element.fromUserId.split('@')[0]
            : element?.fromUserId || '';
        if (elementId !== targetId) return element;
        return { ...element, unreadCount: 0, unread: 0, unreadMessageId: '' };
    });
};

const markRecentSeenAll = (data, stateData) => {
    const targetId = (data?.fromUserId || '').includes('@')
        ? data.fromUserId.split('@')[0]
        : data?.fromUserId || '';
    const localUserId = getLoggedInUserIdSafe();
    if (!targetId || !Array.isArray(stateData)) return stateData;
    return stateData.map(element => {
        const elementId = (element?.fromUserId || '').includes('@')
            ? element.fromUserId.split('@')[0]
            : element?.fromUserId || '';
        if (elementId !== targetId) return element;
        const isMine =
            element.isLastMessageSentByMe ||
            (element.publisherId && getUserIdFromJid(element.publisherId) === localUserId);
        if (!isMine || element.msgStatus === MSG_SEEN_STATUS_ID) return element;
        return { ...element, msgStatus: MSG_SEEN_STATUS_ID };
    });
};

const updateRecentChatMessageStatus = (data, stateData) => {
    return stateData?.map(element => {
        if (element.fromUserId === data.fromUserId && element.msgId === data.msgId) {
            element.msgStatus = getMsgStatusInOrder(element.msgStatus, data.msgStatus);
        }
        if (!element.msgType || element.msgType === undefined) {
            element.msgType = element.msgBody.message_type;
        }
        return element;
    });
};

const updateArchiveRecentChat = (data, stateData) => {
    return stateData.map(element => {
        if (element.fromUserId === data.fromUserId) {
            element.archiveStatus = data.isArchived ? 1 : 0;
        }
        return element;
    });
};

const updateRecentChatReaction = (data, stateData) => {
    if (!stateData) return [];
    const { fromUserId, msgId, emojiId, userId, action, msgBody, msgType, lastMessage, messageSenderId, timestamp } =
        data;
    const normalizedChatId = fromUserId.includes('@') ? fromUserId.split('@')[0] : fromUserId;
    return stateData.map(element => {
        const elementId = element.fromUserId.includes('@') ? element.fromUserId.split('@')[0] : element.fromUserId;
        if (elementId === normalizedChatId) {
            const newReactions = updateReactionList(element.reactions, emojiId, userId, action);
            const hasReactions = newReactions.length > 0;

            if (action === 'add' || action === 'update') {
                // Save snapshot on first reaction ever (identified by no existing snapshot).
                // ONLY save if element.msgBody is valid - otherwise the snapshot is corrupted.
                const canSaveSnapshot = !element.prevMsgId && element.msgBody;
                const prevSnapshot = canSaveSnapshot
                    ? {
                          prevMsgId: element.msgId,
                          prevMsgBody: element.msgBody,
                          prevMsgType: element.msgType,
                          prevTimestamp: element.timestamp,
                      }
                    : {};

                const lastReactionGroup = newReactions[newReactions.length - 1];
                const finalReactedUser = lastReactionGroup?.userIds
                    ? lastReactionGroup.userIds[lastReactionGroup.userIds.length - 1]
                    : userId;
                const finalEmojiId = lastReactionGroup?.emojiId || emojiId;

                const statusTypes = [
                    'Processing',
                    'delivered',
                    'carbonDelivered',
                    'seen',
                    'carbonSeen',
                    'acknowledge',
                    'carbonSentSeen',
                ];
                const currentIsStatus = statusTypes.includes(element.msgType) || !hasBodyContent(element.msgBody);
                const incomingHasContent = hasBodyContent(msgBody);

                return {
                    ...element,
                    ...prevSnapshot,
                    msgId: msgId || element.msgId,
                    msgBody: incomingHasContent || !element.msgBody ? msgBody || element.msgBody : element.msgBody,
                    msgType: msgType && !statusTypes.includes(msgType) ? msgType : element?.msgBody?.message_type,
                    reactions: newReactions,
                    reactedUserName: finalReactedUser,
                    emojiId: finalEmojiId,
                    messageSenderId: messageSenderId ? messageSenderId : null,
                };
            }

            if (action === 'remove') {
                if (!hasReactions) {
                    const fallback = lastMessage || null;

                    const statusTypes = [
                        'Processing',
                        'delivered',
                        'carbonDelivered',
                        'seen',
                        'carbonSeen',
                        'acknowledge',
                        'carbonSentSeen',
                    ];
                    const restoredMsgId = fallback?.msgId || element.prevMsgId || element.msgId;

                    // Priority: 1) Valid fallback from action, 2) Valid snapshot, 3) Current (last resort)
                    let restoredMsgBody = fallback?.msgBody;
                    if (!hasBodyContent(restoredMsgBody)) restoredMsgBody = element.prevMsgBody;
                    if (!hasBodyContent(restoredMsgBody)) restoredMsgBody = element.msgBody;

                    let restoredMsgType =
                        fallback?.msgBody?.message_type || fallback?.msgType || element.prevMsgType || element.msgType;
                    if (statusTypes.includes(restoredMsgType)) restoredMsgType = 'text';

                    const restoredTimestamp = fallback?.timestamp || element.prevTimestamp || element.timestamp;
                    const restoredReactions = fallback?.reactions || [];
                    const lastRG = restoredReactions[restoredReactions.length - 1];
                    const restoredReactedUser = lastRG?.userIds ? lastRG.userIds[lastRG.userIds.length - 1] : '';
                    const restoredEmojiId = lastRG?.emojiId || '';

                    return {
                        ...element,
                        msgId: restoredMsgId || element.msgId,
                        msgBody: restoredMsgBody || {},
                        msgType: restoredMsgType,
                        timestamp: restoredTimestamp,
                        reactions: restoredReactions,
                        reactedUserName: restoredReactedUser,
                        emojiId: restoredEmojiId,
                        messageSenderId: messageSenderId ? messageSenderId : null,
                        prevMsgId: undefined,
                        prevMsgBody: undefined,
                        prevMsgType: undefined,
                        prevTimestamp: undefined,
                        prevReactions: undefined,
                        prevReactedUserName: undefined,
                        prevEmojiId: undefined,
                    };
                } else {
                    // Some reactions remain — update display metadata only
                    const lastReactionGroup = newReactions[newReactions.length - 1];
                    const finalReactedUser = lastReactionGroup?.userIds
                        ? lastReactionGroup.userIds[lastReactionGroup.userIds.length - 1]
                        : '';
                    const finalEmojiId = lastReactionGroup?.emojiId || '';
                    return {
                        ...element,
                        reactions: newReactions,
                        reactedUserName: finalReactedUser,
                        emojiId: finalEmojiId,
                        messageSenderId: messageSenderId ? messageSenderId : null,
                    };
                }
            }
        }
        return element;
    });
};

const updateReconnectRecentChat = (newRecentChat, oldRecentChat = []) => {
    const pendingMsgChats = oldRecentChat.filter(
        chat => chat.msgStatus === 3 && MEDIA_MESSAGE_TYPES.includes(chat.msgType),
    );

    return newRecentChat.map(recentItem => {
        const pendingChat = pendingMsgChats.find(pending => pending.fromUserId === recentItem.fromUserId);
        if (pendingChat) {
            return {
                ...pendingChat,
                ...recentItem,
            };
        } else {
            return {
                ...recentItem,
            };
        }
    });
};

const deletedChatList = (deleteData, currentArray) => {
    const { fromUserId } = deleteData;
    return currentArray.filter((element, id) => element['fromUserId'] !== fromUserId);
};

const clearMessageInRecentChat = (data, id) => {
    return data.find(element => {
        if (element.recent.fromUserId === id) {
            element.recent.msgBody = {};
            element.recent.msgType = '';
            element.recent.createdAt = '';
            element.recent.msgStatus = 4;
            element.recent.reactions = [];
            element.recent.reactedUserName = '';
            element.recent.emojiId = '';
            element.recent.messageSenderId = '';
            return element;
        }
        return false;
    });
};

const updateMsgByLastMsgId = (newMessage, recentChatList = []) => {
    const msgIndex = recentChatList.findIndex(msg => msg.lastMsgId === newMessage.msgId);
    if (msgIndex > -1) {
        recentChatList[msgIndex] = { ...recentChatList[msgIndex], ...newMessage };
        recentChatList[msgIndex]['msgType'] = newMessage?.msgBody?.message_type || newMessage.msgType;
    }
    return recentChatList.sort((a, b) => (b.msgId > a.msgId ? 1 : -1));
};

const clearAllRecentChat = stateData => {
    return stateData.map(element => {
        if (element) {
            element.msgBody = {};
            element.msgType = '';
            element.createdAt = '';
            element.msgStatus = 4;
            element.reactions = [];
            element.reactedUserName = '';
            element.emojiId = '';
            element.messageSenderId = '';
            return element;
        }
        return element;
    });
};

export function RecentChatReducer(state = [], action = {}) {
    let { payload: { id, data, filterBy, rosterData, refreshUnreadCount } = {} } = action;
    refreshUnreadCount = !!refreshUnreadCount;
    let isServerReconnect = !!data?.serverReconnect;
    switch (action.type) {
        case RECENT_CHAT_DATA:
            // Deduplicate the incoming payload first so that API-level duplicates
            // (e.g. the same group appearing more than once) are removed before we
            // merge or store the data.  This covers the initial load, server-reconnect
            // and pagination paths alike.
            if (action.payload.data && Array.isArray(action.payload.data)) {
                action.payload.data = dedupeRecentChatsByConversation(action.payload.data);
            }
            if (state.data && !isServerReconnect) {
                let constructData = [...state.data, ...action.payload.data];
                const latestObjects = constructData.reduce((acc, obj) => {
                    const normalizedId = obj.fromUserId.includes('@') ? obj.fromUserId.split('@')[0] : obj.fromUserId;
                    const prev = acc[normalizedId];
                    if (!prev) {
                        acc[normalizedId] = { ...obj, fromUserId: normalizedId };
                        return acc;
                    }
                    const merged =
                        (prev.timestamp || 0) < (obj.timestamp || 0)
                            ? { ...obj, fromUserId: normalizedId }
                            : { ...prev };
                    const unreadSrc = obj.unreadCount != null ? obj : prev;
                    if (unreadSrc.unreadCount != null) merged.unreadCount = unreadSrc.unreadCount;
                    if (unreadSrc.unreadMessageId) merged.unreadMessageId = unreadSrc.unreadMessageId;
                    acc[normalizedId] = merged;
                    return acc;
                }, {});
                action.payload.data = Object.values(latestObjects);
            }
            if (action.payload.data && Array.isArray(action.payload.data)) {
                action.payload.data = action.payload.data.map(recent => ({
                    ...recent,
                    fromUserId: recent.fromUserId.includes('@') ? recent.fromUserId.split('@')[0] : recent.fromUserId,
                    reactions: transformReactionsGrouped(recent.reactions),
                }));
            }
            return action.payload;
        case RECONNECT_RECENT_CHAT_UPDATE:
            return {
                ...state,
                id,
                data: updateReconnectRecentChat(action.payload?.data, state.data),
                refreshUnreadCount,
            };
        case UPDATE_MESSAGE_STATUS:
            return {
                ...state,
                id,
                data: updateRecentChatMessageStatus(action.payload?.data, state.data),
                refreshUnreadCount,
            };
        case UPDATE_MESSAGE_REACTION:
            return {
                ...state,
                id,
                data: updateRecentChatReaction(action.payload?.data, state.data),
                refreshUnreadCount,
            };
        case ACTIVE_CHAT_UPDATE:
            return {
                ...state,
                id,
                data: updateRecentChat(filterBy, data, state.data),
                refreshUnreadCount,
            };
        case RECENT_NOTIFICATION_UPDATE:
            return {
                ...state,
                data: updateRecentChat(filterBy, data, state.data),
                refreshUnreadCount,
            };
        case RECENT_REMOVE_MESSAGE_UPDATE:
            return {
                ...state,
                id,
                data: emptyMessage(data, state.data),
                refreshUnreadCount,
            };
        case RECENT_RECALL_UPDATE:
            return {
                ...state,
                id,
                data: updateRecall(data, state.data),
                refreshUnreadCount,
            };

        case RECENT_ROSTER_UPDATE:
            return {
                ...state,
                rosterData,
                refreshUnreadCount,
            };
        case DELETE_SINGLE_CHAT:
            return {
                ...state,
                id,
                data: deletedChatList(data, state.data),
                refreshUnreadCount,
            };
        case UPDATE_ROSTER_LAST_MESSAGE:
            const recentChatNames = state?.rosterData?.recentChatNames || [];
            const recentChatItems = state?.rosterData?.recentChatItems || [];

            return {
                ...state,
                id,
                ...state.data,
                rosterData: [...recentChatNames, clearMessageInRecentChat(recentChatItems, data)],
                refreshUnreadCount,
            };
        case RESET_RECENT_CHAT:
            return [];
        case UPDATE_MSG_BY_LAST_MSGID:
            return {
                ...state,
                id,
                data: updateMsgByLastMsgId(data, state.data),
                refreshUnreadCount,
            };
        case UPDATE_MUTE_RECENT_CHAT:
            return {
                ...state,
                id,
                data: updateMuteRecentChat(data, state.data),
                refreshUnreadCount,
            };
        case UPDATE_ARCHIVE_RECENT_CHAT:
            return {
                ...state,
                id,
                data: updateArchiveRecentChat(data, state.data),
                refreshUnreadCount,
            };
        case UNREAD_MESSAGE_DELETE:
            return {
                ...state,
                id,
                data: updateRecentUnreadCount(data, state.data),
                refreshUnreadCount,
            };
        case MARK_CHAT_MESSAGES_SEEN:
            return {
                ...state,
                id,
                data: markRecentSeenAll(data, state.data),
                refreshUnreadCount,
            };
        case CLEAR_ALL_CHAT:
            return {
                ...state,
                id,
                data: clearAllRecentChat(state.data),
            };

        case CLEAR_CHAT_HISTORY:
            return {
                ...state,
                id,
                data: state.data.map(element => {
                    const elementId = element.fromUserId.includes('@')
                        ? element.fromUserId.split('@')[0]
                        : element.fromUserId;
                    const targetId = action.payload.data.fromUserId.includes('@')
                        ? action.payload.data.fromUserId.split('@')[0]
                        : action.payload.data.fromUserId;
                    if (elementId === targetId) {
                        return {
                            ...element,
                            msgBody: {},
                            msgType: '',
                            createdAt: '',
                            msgStatus: 4,
                            reactions: [],
                            reactedUserName: '',
                            emojiId: '',
                            messageSenderId: '',
                        };
                    }
                    return element;
                }),
            };

        default:
            return state;
    }
}

export function ActiveChatReducer(state = [], action = {}) {
    let { payload } = action;
    if (action.type === ACTIVE_CHAT_DATA && payload?.data?.recent) {
        payload.data.recent.reactions = transformReactionsGrouped(payload.data.recent.reactions);
    }
    switch (action.type) {
        case ACTIVE_CHAT_DATA:
            return action.payload;
        case UPDATE_GROUP_NAME: {
            const { nickName } = action.payload.data.groups[0];
            const updatedData = state.data?.roster || {};
            updatedData['nickName'] = nickName;
            return {
                ...state,
                id: action.payload.id,
                ...state.data,
                roster: updatedData,
            };
        }
        case UPDATE_MUTE_RECENT_CHAT:
            if (!state.data) return state;
            {
                const {
                    chatId,
                    recent: { muteStatus },
                } = state.data || {};
                const { fromUserId, isMuted } = action.payload.data;
                const newMuteStatus = chatId === fromUserId ? Number(isMuted) : muteStatus;
                return {
                    ...state,
                    id: uuidv4(),
                    data: {
                        ...state.data,
                        recent: {
                            ...state.data.recent,
                            muteStatus: newMuteStatus,
                        },
                    },
                };
            }
        case UPDATE_ARCHIVE_RECENT_CHAT:
            if (!state.data) return state;
            {
                const {
                    chatId,
                    recent: { archiveStatus },
                } = state.data || {};
                const { fromUserId, isArchived } = action.payload.data;
                const newArchiveStatus = chatId === fromUserId ? Number(isArchived) : archiveStatus;
                return {
                    ...state,
                    id: uuidv4(),
                    data: {
                        ...state.data,
                        recent: {
                            ...state.data.recent,
                            archiveStatus: newArchiveStatus,
                        },
                    },
                };
            }
        case UPDATE_MESSAGE_REACTION:
            if (!state.data) return state;
            {
                const {
                    fromUserId,
                    msgId,
                    emojiId,
                    userId,
                    action: reactionAction,
                    msgBody,
                    msgType,
                    lastMessage,
                    timestamp,
                    messageSenderId,
                } = action.payload.data;
                const { chatId, recent } = state.data;
                if (chatId === fromUserId) {
                    const hadNoReactions = !recent.reactions || recent.reactions.length === 0;
                    const newReactions = updateReactionList(recent.reactions, emojiId, userId, reactionAction);
                    const hasReactions = newReactions.length > 0;

                    if (reactionAction === 'add' || reactionAction === 'update') {
                        const prevSnapshot = hadNoReactions
                            ? {
                                  prevMsgId: recent.msgId,
                                  prevMsgBody: recent.msgBody,
                                  prevMsgType: recent.msgType,
                                  prevTimestamp: recent.timestamp,
                              }
                            : {};

                        const fallback = lastMessage || null;
                        const restoredMsgId = fallback?.msgId || msgId || recent.msgId;
                        const restoredMsgBody = fallback?.msgBody || msgBody || recent.msgBody;
                        const restoredMsgType =
                            fallback?.msgType || fallback?.msgBody?.message_type || msgType || recent.msgType;
                        const restoredTimestamp =
                            fallback?.activityTime || fallback?.timestamp || timestamp || recent.timestamp;
                        const restoredReactions = fallback?.reactions || newReactions;

                        const lastReactionGroup = restoredReactions[restoredReactions.length - 1];
                        const restoredReactedUser = lastReactionGroup?.userIds
                            ? lastReactionGroup.userIds[lastReactionGroup.userIds.length - 1]
                            : fallback?.reactedUserName || userId;
                        const restoredEmojiId = lastReactionGroup?.emojiId || fallback?.emojiId || emojiId;

                        return {
                            ...state,
                            id: uuidv4(),
                            data: {
                                ...state.data,
                                recent: {
                                    ...recent,
                                    ...prevSnapshot,
                                    msgId: restoredMsgId,
                                    msgBody: restoredMsgBody,
                                    msgType: restoredMsgType,
                                    // timestamp: restoredTimestamp,
                                    reactions: restoredReactions,
                                    reactedUserName: restoredReactedUser,
                                    emojiId: restoredEmojiId,
                                    messageSenderId: messageSenderId || recent.messageSenderId,
                                },
                            },
                        };
                    }

                    if (reactionAction === 'remove') {
                        if (!hasReactions) {
                            const fallback = lastMessage || null;
                            const restoredMsgId = fallback?.msgId || recent.prevMsgId || recent.msgId;
                            const restoredMsgBody = fallback?.msgBody || recent.prevMsgBody || recent.msgBody;
                            const restoredMsgType =
                                fallback?.msgBody?.message_type ||
                                fallback?.msgType ||
                                recent.prevMsgType ||
                                recent.msgBody?.msgType;
                            const restoredTimestamp =
                                fallback?.activityTime ||
                                fallback?.timestamp ||
                                timestamp ||
                                recent.prevTimestamp ||
                                recent.timestamp;
                            const restoredReactions = fallback?.reactions || [];

                            const lastReactionGroup = restoredReactions[restoredReactions.length - 1];
                            const restoredReactedUser = lastReactionGroup?.userIds
                                ? lastReactionGroup.userIds[lastReactionGroup.userIds.length - 1]
                                : fallback?.reactedUserName || '';
                            const restoredEmojiId = lastReactionGroup?.emojiId || fallback?.emojiId || '';
                            const statusTypes = [
                                'Processing',
                                'delivered',
                                'carbonDelivered',
                                'seen',
                                'carbonSeen',
                                'acknowledge',
                                'carbonSentSeen',
                            ];

                            return {
                                ...state,
                                id: uuidv4(),
                                data: {
                                    ...state.data,
                                    recent: {
                                        ...recent,
                                        msgId: restoredMsgId,
                                        msgBody: hasBodyContent(restoredMsgBody)
                                            ? restoredMsgBody
                                            : recent.msgBody || {},
                                        msgType:
                                            restoredMsgType && !statusTypes.includes(restoredMsgType)
                                                ? restoredMsgType
                                                : recent.msgBody?.msgType || 'text',
                                        // timestamp: restoredTimestamp,
                                        reactions: restoredReactions,
                                        reactedUserName: restoredReactedUser,
                                        emojiId: restoredEmojiId,
                                        messageSenderId: messageSenderId || recent.messageSenderId,
                                        prevMsgId: undefined,
                                        prevMsgBody: undefined,
                                        prevMsgType: undefined,
                                        prevTimestamp: undefined,
                                    },
                                },
                            };
                        } else {
                            const fallback = lastMessage || null;
                            const restoredMsgId = fallback?.msgId || recent.msgId;
                            const restoredMsgBody = fallback?.msgBody || recent.msgBody;
                            const restoredMsgType =
                                fallback?.msgType || fallback?.msgBody?.message_type || recent.msgType;
                            const restoredTimestamp =
                                fallback?.activityTime || fallback?.timestamp || timestamp || recent.timestamp;
                            const restoredReactions = fallback?.reactions || newReactions;

                            const lastReactionGroup = restoredReactions[restoredReactions.length - 1];
                            const restoredReactedUser = lastReactionGroup?.userIds
                                ? lastReactionGroup.userIds[lastReactionGroup.userIds.length - 1]
                                : fallback?.reactedUserName || '';
                            const restoredEmojiId = lastReactionGroup?.emojiId || fallback?.emojiId || '';

                            return {
                                ...state,
                                id: uuidv4(),
                                data: {
                                    ...state.data,
                                    recent: {
                                        ...recent,
                                        msgId: restoredMsgId,
                                        msgBody: restoredMsgBody,
                                        msgType: restoredMsgType,
                                        // timestamp: restoredTimestamp,
                                        reactions: restoredReactions,
                                        reactedUserName: restoredReactedUser,
                                        emojiId: restoredEmojiId,
                                        messageSenderId: messageSenderId || recent.messageSenderId,
                                    },
                                },
                            };
                        }
                    }
                }
                return state;
            }
        case UPDATE_ACTIVE_CHAT_DATA:
            if (!state.data) return state;
            {
                const isSameMsg =
                    action.payload.data.msgId &&
                    state.data.recent.msgId &&
                    String(action.payload.data.msgId) === String(state.data.recent.msgId);
                const statusTypes = [
                    'Processing',
                    'delivered',
                    'carbonDelivered',
                    'seen',
                    'carbonSeen',
                    'acknowledge',
                    'carbonSentSeen',
                ];
                const isStatusType = statusTypes.includes(action.payload.data.msgType);
                const hasNewContent = hasBodyContent(action.payload.data.msgBody);

                const reactionClearing = isSameMsg
                    ? {}
                    : {
                          reactions: undefined,
                          reactedUserName: undefined,
                          emojiId: undefined,
                          messageSenderId: undefined,
                      };
                return {
                    ...state,
                    id: uuidv4(),
                    data: {
                        ...state.data,
                        recent: {
                            ...state.data.recent,
                            ...reactionClearing,
                            ...action.payload.data,
                            msgBody: hasNewContent ? action.payload.data.msgBody : state.data.recent.msgBody || {},
                            msgType:
                                action.payload.data.msgType && !isStatusType
                                    ? action.payload.data.msgType
                                    : state.data.recent.msgType || action.payload.data.msgType,
                        },
                    },
                };
            }

        case RESET_TO_DEFAULT:
            return [];
        default:
            return state;
    }
}

export function WebChatProfileImageReducer(state = [], action = {}) {
    if (action.type === PROFILE_IMAGE_CHAT_DATA) {
        return action.payload;
    }
    return state;
}

const initialState = {
    modalPopup: false,
};
export function modalPopUpReducer(state = initialState, action = {}) {
    if (action.type === MODAL_ACTIVE_CLASS) {
        return {
            ...state,
            modalPopup: action.payload,
        };
    }
    return state;
}
